import type { Cue } from '../../core/model/cue.ts';
import {
  buildDubbingTape,
  centerXWithOffset,
  cueScreenLeftX,
  dubbingScale,
  pastClipWidth,
  tapePlayheadX,
} from '../../core/dubbing/layout.ts';
import { getShadowingCenterOffsetPx } from '../../core/dubbing/shadowingSession.ts';
import {
  clampTopToFloor,
  clampUnifiedTextSlotTopsToFloor,
  computeUnifiedTextSlotTops,
  subtitleContentFloor,
} from '../../core/layout.ts';
import { paintTrackStyle, syncGoogleFont } from '../../core/styles/applyTrackStyle.ts';
import { trackStyleFromSettings } from '../../core/styles/stylePresets.ts';
import { toTrustedHtml } from './trustedTypes.ts';
import { SUBTITLE_DRAG_HANDLE_CLASS } from './subtitlePositionDrag.ts';
import type { OverlayRenderModel } from './overlayRenderer.ts';

const PAST_COLOR = '#9e9e9e';
const STRUCTURE_ATTR = 'data-unisubs-dubbing-sig';

export type DubbingPaintDeps = {
  loadingText: string;
  getRenderText: (
    cue: Cue,
    dualMode: boolean,
    options: { loadingText: string; speakingMode: boolean },
  ) => string;
  syncTextDragHandle: (container: HTMLElement, model: OverlayRenderModel) => void;
  onTextRendered?: (html: string, slot: OverlayRenderModel['slot'], cue: Cue) => void;
};

function structureSignature(
  cues: Cue[],
  dualMode: boolean,
  speakingMode: boolean,
  grayPast: boolean,
  getRenderText: DubbingPaintDeps['getRenderText'],
): string {
  const cueSig = cues
    .map((cue) => {
      const text = getRenderText(cue, dualMode, {
        loadingText: '',
        speakingMode,
      });
      return `${cue.start}\x1f${cue.end}\x1f${text}`;
    })
    .join('\x1e');
  // Rebuild when gray twin is added/removed so toggle is not clip-only.
  return `${grayPast ? '1' : '0'}\x1d${cueSig}`;
}

function measureCueHeight(track: HTMLElement): number {
  const first = track.querySelector('.unisubs-dubbing-base') as HTMLElement | null;
  return first?.offsetHeight ?? 0;
}

function applyCueChrome(
  el: HTMLElement,
  trackStyle: ReturnType<typeof trackStyleFromSettings>,
  model: OverlayRenderModel,
  gray: boolean,
): void {
  paintTrackStyle(el, trackStyle, {
    viewHeight: model.baseline.viewHeight,
    maxWidthPx: undefined,
    googleSlot: model.slot,
  });
  el.style.whiteSpace = 'pre';
  el.style.width = 'max-content';
  el.style.maxWidth = 'none';
  el.style.minWidth = 'max-content';
  el.style.display = 'block';
  el.style.boxSizing = 'border-box';
  el.style.left = 'auto';
  el.style.right = 'auto';
  if (gray) {
    el.style.color = PAST_COLOR;
    el.style.webkitTextStrokeColor = PAST_COLOR;
    el.style.opacity = String(Math.min(trackStyle.opacity, 0.85));
  }
}

/**
 * Reveal gray overlay for the portion left of center via clip-path.
 * Past stays full natural width (never shrink-wraps inside a clip box — that
 * caused wrapped/garbled glyphs once pastW narrowed).
 */
function applyPastReveal(past: HTMLElement, baseWidth: number, pastW: number): void {
  const reveal = Math.max(0, Math.min(baseWidth, pastW));
  const clipRight = Math.max(0, baseWidth - reveal);
  past.style.clipPath = reveal <= 0
    ? 'inset(0 100% 0 0)'
    : `inset(0 ${clipRight}px 0 0)`;
  // Safari / older Chromium
  past.style.setProperty('-webkit-clip-path', past.style.clipPath);
}

/**
 * Show only the still-upcoming (right-of-center) slice of the colored base.
 * Complementary to applyPastReveal so gray never stacks on top of color
 * (which made past look darker when hide-upcoming was off).
 */
function applyUpcomingReveal(base: HTMLElement, baseWidth: number, pastW: number): void {
  const hideLeft = Math.max(0, Math.min(baseWidth, pastW));
  base.style.clipPath = hideLeft <= 0
    ? 'none'
    : hideLeft >= baseWidth
      ? 'inset(0 0 0 100%)'
      : `inset(0 0 0 ${hideLeft}px)`;
  base.style.setProperty('-webkit-clip-path', base.style.clipPath === 'none' ? 'none' : base.style.clipPath);
}

/** Paint or update the horizontal dubbing ticker for one subtitle slot. */
export function paintDubbingOverlay(
  container: HTMLElement,
  model: OverlayRenderModel,
  currentTimeMs: number,
  deps: DubbingPaintDeps,
): void {
  const { settings, baseline, cues } = model;
  const trackStyle = trackStyleFromSettings(settings, model.slot);
  const spacing = baseline.viewHeight * settings.spacing;
  const { approachScale } = dubbingScale({
    viewWidth: baseline.viewWidth,
    spaceWidth: baseline.spaceWidth,
    currentTimeMs,
  });
  const centerX = centerXWithOffset(
    baseline.spaceWidth,
    baseline.viewWidth,
    getShadowingCenterOffsetPx(model.slot, Boolean(settings.subtitleSeparation)),
  );

  // Map viewport-local layout into the overlay host. On Netflix, #mainSub lives
  // under `.watch-video` while size was measured from `<video>` / player-view —
  // without this origin, spaceWidth alone leaves empty gutters on both sides.
  const host = (container.offsetParent as HTMLElement | null) ?? container.parentElement;
  const originX = model.playbackClientRect && host
    ? model.playbackClientRect.left - host.getBoundingClientRect().left
    : 0;
  const originY = model.playbackClientRect && host
    ? model.playbackClientRect.top - host.getBoundingClientRect().top
    : 0;

  container.classList.add('unisubs-dubbing');
  container.style.inset = '';
  container.style.right = '';
  container.style.left = `${originX + baseline.spaceWidth}px`;
  container.style.width = `${baseline.viewWidth}px`;
  container.style.flexFlow = 'column';
  // Visible so the Y drag handle (sits above the box) is not clipped; the
  // track itself still clips ticker overflow.
  container.style.overflow = 'visible';

  syncGoogleFont(
    model.slot,
    trackStyle.fontGoogleFamilies,
    trackStyle.fontGoogleCssProperties,
    document,
  );

  const grayPast = settings.shadowingGrayPast !== false;
  const sig = structureSignature(
    cues,
    model.dualMode,
    settings.speakingMode,
    grayPast,
    deps.getRenderText,
  );
  let track = container.querySelector('.unisubs-dubbing-track') as HTMLElement | null;

  if (!track || container.getAttribute(STRUCTURE_ATTR) !== sig) {
    container.replaceChildren();
    track = document.createElement('div');
    track.className = 'unisubs-dubbing-track';
    track.style.position = 'relative';
    track.style.width = '100%';
    track.style.overflow = 'hidden';
    container.appendChild(track);

    cues.forEach((cue) => {
      const block = document.createElement('div');
      block.className = 'unisubs-dubbing-cue';
      block.dataset.start = String(cue.start);
      block.dataset.end = String(cue.end);
      block.style.position = 'absolute';
      block.style.top = '0';
      block.style.left = '0';
      block.style.margin = '0';
      block.style.padding = '0';
      block.style.background = 'transparent';
      block.style.whiteSpace = 'pre';
      block.style.width = 'max-content';
      block.style.maxWidth = 'none';
      block.style.pointerEvents = 'auto';
      block.style.cursor = 'grab';

      const html = deps.getRenderText(cue, model.dualMode, {
        loadingText: deps.loadingText,
        speakingMode: settings.speakingMode,
      });

      const base = document.createElement('div');
      base.className = 'unisubs-dubbing-base textBasedSub';
      base.dir = 'auto';
      base.innerHTML = toTrustedHtml(html);
      applyCueChrome(base, trackStyle, model, false);
      base.style.position = 'relative';

      block.append(base);
      // Gray twin only when enabled — removing it makes the toggle obvious.
      if (grayPast) {
        const past = document.createElement('div');
        past.className = 'unisubs-dubbing-past textBasedSub';
        past.dir = 'auto';
        past.innerHTML = toTrustedHtml(html);
        applyCueChrome(past, trackStyle, model, true);
        past.style.position = 'absolute';
        past.style.left = '0';
        past.style.top = '0';
        past.style.pointerEvents = 'none';
        past.style.clipPath = 'inset(0 100% 0 0)';
        past.style.setProperty('-webkit-clip-path', 'inset(0 100% 0 0)');
        block.append(past);
      }
      track!.appendChild(block);
      deps.onTextRendered?.(html, model.slot, cue);
    });

    container.setAttribute(STRUCTURE_ATTR, sig);
  }

  // Keep the vertical position grip in sync every paint (same as watch), not
  // only when the cue set rebuilds — shared-subY dual slots need both paints.
  deps.syncTextDragHandle(container, model);
  // Sit this slot's grip on its (temporary) center line. Separated tracks keep
  // independent offsets; shared mode still uses one value for both paints.
  const localCenter = centerX - baseline.spaceWidth;
  const handle = container.querySelector(`.${SUBTITLE_DRAG_HANDLE_CLASS}`) as HTMLElement | null;
  if (handle) handle.style.left = `${localCenter}px`;

  const blocks = [...track.querySelectorAll('.unisubs-dubbing-cue')] as HTMLElement[];
  const measured = blocks.map((block) => {
    const start = Number(block.dataset.start);
    const end = Number(block.dataset.end);
    const base = block.querySelector('.unisubs-dubbing-base') as HTMLElement | null;
    const width = base?.offsetWidth ?? block.offsetWidth;
    return { block, start, end, width };
  });

  const tape = buildDubbingTape(
    measured.map(({ start, end, width }) => ({ start, end, width })),
    approachScale,
  );
  const playheadX = tapePlayheadX(tape, currentTimeMs, approachScale);
  const leftByStart = new Map(
    tape.map((placement) => [
      placement.start,
      cueScreenLeftX(placement.tapeStart, playheadX, centerX),
    ] as const),
  );

  let maxHeight = 0;
  const contentLeft = baseline.spaceWidth;

  measured.forEach(({ block, start, width }) => {
    const left = leftByStart.get(start)
      ?? cueScreenLeftX(0, playheadX, centerX);
    // Do NOT visibility-hide cues still to the right of the content box.
    // Hiding until `left < contentRight` pops the full measured block mid-approach
    // once layout width becomes non-zero (looks like text suddenly appearing on
    // the right). Keep them visible and let the track's overflow:hidden clip the
    // off-screen approach/exit instead.
    const fullyPastLeft = left + width <= contentLeft;
    block.style.visibility = fullyPastLeft ? 'hidden' : 'visible';
    block.style.transform = `translateX(${left - baseline.spaceWidth}px)`;

    const base = block.querySelector('.unisubs-dubbing-base') as HTMLElement | null;
    const past = block.querySelector('.unisubs-dubbing-past') as HTMLElement | null;
    const baseWidth = base?.offsetWidth ?? width;
    const pastW = pastClipWidth(left, baseWidth, centerX);
    if (past) applyPastReveal(past, past.offsetWidth || baseWidth, pastW);
    if (base) {
      // Hide-upcoming + gray: only gray past. Hide-upcoming alone: color clip to
      // the right of center. Gray alone: complementary clips. Neither: full color.
      if (settings.shadowingHideUpcoming && grayPast) {
        base.style.visibility = 'hidden';
        base.style.clipPath = 'none';
        base.style.setProperty('-webkit-clip-path', 'none');
      } else if (settings.shadowingHideUpcoming || grayPast) {
        base.style.visibility = 'visible';
        applyUpcomingReveal(base, baseWidth, pastW);
      } else {
        base.style.visibility = 'visible';
        base.style.clipPath = 'none';
        base.style.setProperty('-webkit-clip-path', 'none');
      }
    }

    maxHeight = Math.max(maxHeight, base?.offsetHeight ?? block.offsetHeight);
  });

  if (maxHeight === 0) maxHeight = measureCueHeight(track);
  track.style.height = `${maxHeight}px`;

  const floorY = subtitleContentFloor(baseline.viewBottom);
  const totalHeight = maxHeight;

  if (settings.subtitleSeparation) {
    let top: number;
    if (model.slot === 'main') {
      top = model.hasOtherSlot
        ? baseline.top - totalHeight - spacing
        : baseline.top - totalHeight / 2;
    } else {
      top = model.hasOtherSlot
        ? baseline.top + spacing
        : baseline.top - totalHeight / 2;
    }
    container.style.top = `${originY + clampTopToFloor(top, totalHeight, floorY)}px`;
    return;
  }

  const mainEl = document.getElementById('mainSub');
  const secondEl = document.getElementById('secondSub');
  const mainHeight = model.slot === 'main'
    ? totalHeight
    : (model.hasOtherSlot
      ? (mainEl?.querySelector('.unisubs-dubbing-track') as HTMLElement | null)?.offsetHeight ?? 0
      : null);
  const secondHeight = model.slot === 'second'
    ? totalHeight
    : (model.hasOtherSlot
      ? (secondEl?.querySelector('.unisubs-dubbing-track') as HTMLElement | null)?.offsetHeight ?? 0
      : null);

  const tops = clampUnifiedTextSlotTopsToFloor(
    computeUnifiedTextSlotTops({
      baselineTop: baseline.top,
      spacing,
      mainHeight,
      secondHeight,
    }),
    { mainHeight, secondHeight },
    floorY,
  );

  if (tops.mainTop !== undefined && mainEl) {
    mainEl.style.top = `${originY + tops.mainTop}px`;
  }
  if (tops.secondTop !== undefined && secondEl) {
    secondEl.style.top = `${originY + tops.secondTop}px`;
  }
}
